import { isAxiosError } from 'axios';
import type { Boat } from '../types/boat.types';
import {
  buildUpdatePayload,
  diffBoat,
  mapErrorPaths,
  rebase,
  type EditableBoat,
  type FieldConflict,
  type LostRow,
  type ScalarValue,
} from '../utils/boat-changes';

/**
 * #1177 (spec §3.1–§3.3) — send the dealer's changes with the snapshot's
 * version. On 409 STALE_VERSION: refetch, rebase and resend once; if the same
 * field changed on both sides, hand the conflicts back for the dealer to choose.
 */
/**
 * `lostRows` — rows the dealer edited that were removed elsewhere, found by a
 * rebase. Every outcome a rebase can lead to carries them: a rebase can also
 * consume every change (`nothing_to_save`), and the loss must still be reported.
 *
 * `sent` — after a silent rebase-and-resend it holds the colleague's values too,
 * not what the form shows. A caller that stays in edit mode after `saved` must
 * refill the form from `sent` and rebase on `boat`; otherwise the next Save sends
 * the form's older values back as the dealer's (and freezes them). The detail
 * page leaves edit mode and refills from the refetched boat instead.
 */
export type SaveOutcome =
  | { kind: 'nothing_to_save'; lostRows: LostRow[] }
  | { kind: 'saved'; boat: Boat; sent: EditableBoat; lostRows: LostRow[] }
  | { kind: 'conflicts'; fresh: Boat; merged: EditableBoat; conflicts: FieldConflict[]; lostRows: LostRow[] }
  | { kind: 'invalid'; message: string; errors: Record<string, string[]> }
  | { kind: 'stale' };

export interface SaveBoatChangesArgs {
  /** The last server snapshot, normalized. */
  base: EditableBoat;
  /** The form now, normalized. */
  current: EditableBoat;
  /** Keys sent regardless of the diff — the finalize `status_id`. */
  extra?: Record<string, ScalarValue>;
  send: (payload: Record<string, unknown>) => Promise<Boat>;
  fetchFresh: () => Promise<Boat>;
  toEditable: (boat: Boat) => EditableBoat;
  /**
   * S4-39 — applied to a 409's rebased state before it is resent or offered as a
   * conflict. The canvas passes `pairAddedRows`, so an engine the dealer typed
   * becomes an edit to the engine a read created meanwhile, not a second engine.
   */
  afterRebase?: (merged: EditableBoat, base: EditableBoat) => EditableBoat;
}

interface ErrorBody {
  message?: string;
  error_code?: string;
  errors?: Record<string, string[]>;
}

export async function saveBoatChanges(args: SaveBoatChangesArgs): Promise<SaveOutcome> {
  let base = args.base;
  let current = args.current;
  let lostRows: LostRow[] = [];
  const extra = args.extra ?? {};

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const { payload, pathMap, isEmpty } = buildUpdatePayload(diffBoat(base, current), base.version, extra);
    if (isEmpty) return { kind: 'nothing_to_save', lostRows };

    try {
      return { kind: 'saved', boat: await args.send(payload), sent: current, lostRows };
    } catch (error) {
      if (!isAxiosError<ErrorBody>(error)) throw error;
      const status = error.response?.status;
      const body = error.response?.data;

      if (status === 422) {
        return {
          kind: 'invalid',
          message: body?.message ?? 'Some fields are invalid.',
          errors: mapErrorPaths(body?.errors ?? {}, pathMap),
        };
      }
      if (status !== 409 || body?.error_code !== 'STALE_VERSION') throw error;
      if (attempt === 1) return { kind: 'stale' };

      const fresh = await args.fetchFresh();
      const theirs = args.toEditable(fresh);
      const rebased = rebase(base, current, theirs);
      const merged = args.afterRebase ? args.afterRebase(rebased.merged, base) : rebased.merged;
      lostRows = rebased.lostRows;
      if (rebased.conflicts.length > 0) {
        return { kind: 'conflicts', fresh, merged, conflicts: rebased.conflicts, lostRows };
      }
      base = theirs;
      current = merged;
    }
  }

  return { kind: 'stale' };
}
